/**
 * White-labeling / Branding Configuration Types
 *
 * This module defines the TypeScript interfaces for tenant-specific branding.
 * Branding configuration is stored in the platform database (tenants.settings.branding)
 * and loaded at runtime for each tenant.
 */

export interface BrandingLogoConfig {
  /** URL to the logo image (SVG, PNG, or JPG) */
  url: string;
  /** Optional URL to dark mode variant of the logo */
  darkUrl?: string;
  /** Logo height in pixels (default: 40) */
  height?: number;
  /** Logo width in pixels (default: auto) */
  width?: number;
}

export interface BrandingColorConfig {
  /** Main color value (hex, rgb, or hsl) */
  main: string;
  /** Light variant (optional, will be auto-generated if not provided) */
  light?: string;
  /** Dark variant (optional, will be auto-generated if not provided) */
  dark?: string;
  /** Contrast text color (optional, will be auto-determined) */
  contrastText?: string;
}

export interface BrandingColorsConfig {
  /** Primary brand color (buttons, links, active states) */
  primary?: BrandingColorConfig;
  /** Secondary brand color (accents, secondary actions) */
  secondary?: BrandingColorConfig;
}

export interface BrandingTypographyConfig {
  /** Font family name (e.g., "Roboto, sans-serif") */
  fontFamily?: string;
  /** URL to load custom font (e.g., Google Fonts URL) */
  fontUrl?: string;
}

/**
 * Complete branding configuration for a tenant.
 * All fields are optional - if not provided, defaults will be used.
 */
export interface BrandingConfig {
  /** Logo configuration */
  logo?: BrandingLogoConfig;
  /** Favicon URL (recommended: .ico or .svg, 32x32 or 48x48) */
  favicon?: string;
  /** Company/brand name (displayed when logo is not available) */
  companyName?: string;
  /** Brand colors configuration */
  colors?: BrandingColorsConfig;
  /** Typography configuration */
  typography?: BrandingTypographyConfig;
}

/**
 * Support / help link configuration
 */
export interface SupportConfig {
  /** URL to help documentation */
  helpUrl?: string;
  /** Support email address */
  supportEmail?: string;
  /** Display name on customer emails (#3122); defaults to the workshop name. */
  senderName?: string;
  /** Reply-To on customer emails (#3122); defaults to the support or contact email. */
  replyTo?: string;
}

/**
 * Response from the tenant branding API endpoint
 */
export interface TenantBrandingResponse {
  /** Tenant's registered name */
  tenantName: string;
  /** Tenant's URL slug */
  tenantSlug: string;
  /** Branding configuration (null if not configured) */
  branding: BrandingConfig | null;
  /** Support / help link configuration (null if not configured) */
  support: SupportConfig | null;
}

/**
 * Default primary color configuration
 */
const DEFAULT_PRIMARY: Readonly<Required<BrandingColorConfig>> = {
  main: '#228ACF', // OS Buddy — Blue – Abyss (Figma)
  light: '#4FA4DA',
  dark: '#1A6FA6',
  contrastText: '#ffffff',
} as const;

/**
 * Default secondary color configuration
 */
const DEFAULT_SECONDARY: Readonly<Required<BrandingColorConfig>> = {
  main: '#5BD5E6', // OS Buddy — Blue – Shallow (Figma)
  light: '#84E1EE',
  dark: '#2FB6CC',
  contrastText: '#0D171C',
} as const;

/**
 * Default branding values used when no custom branding is configured
 */
export const DEFAULT_BRANDING = {
  companyName: 'Staff Portal',
  colors: {
    primary: DEFAULT_PRIMARY,
    secondary: DEFAULT_SECONDARY,
  },
  typography: {
    fontFamily: '"Outfit Variable", "Outfit", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
  },
} as const;

/**
 * Helper function to merge custom branding with defaults
 */
export function mergeBrandingWithDefaults(
  customBranding: BrandingConfig | null | undefined
): Required<Omit<BrandingConfig, 'logo' | 'favicon'>> & Pick<BrandingConfig, 'logo' | 'favicon'> {
  const customPrimary = customBranding?.colors?.primary;
  const customSecondary = customBranding?.colors?.secondary;

  return {
    logo: customBranding?.logo,
    favicon: customBranding?.favicon,
    companyName: customBranding?.companyName ?? DEFAULT_BRANDING.companyName,
    colors: {
      primary: {
        main: customPrimary?.main ?? DEFAULT_PRIMARY.main,
        light: customPrimary?.light ?? DEFAULT_PRIMARY.light,
        dark: customPrimary?.dark ?? DEFAULT_PRIMARY.dark,
        contrastText: customPrimary?.contrastText ?? DEFAULT_PRIMARY.contrastText,
      },
      secondary: {
        main: customSecondary?.main ?? DEFAULT_SECONDARY.main,
        light: customSecondary?.light ?? DEFAULT_SECONDARY.light,
        dark: customSecondary?.dark ?? DEFAULT_SECONDARY.dark,
        contrastText: customSecondary?.contrastText ?? DEFAULT_SECONDARY.contrastText,
      },
    },
    typography: {
      fontFamily: customBranding?.typography?.fontFamily ?? DEFAULT_BRANDING.typography.fontFamily,
      fontUrl: customBranding?.typography?.fontUrl,
    },
  };
}
